import React, { useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { InputField, LoaderOverlay, MakeToast } from '../../../components';
import {
  BackToLogin,
  SubmitText,
  ForgotPasswordText,
  ForgotPasswordInfo,
} from '../../data/constants';
import { forgotPassword } from '../../service';
import { validateEmail } from '../../../services';
import { forgotPasswordProps } from './forgot-password.props';
import './forgot-password.css';
import { ForgotPassReducer } from '../../../utils/reducer';

const initialState = {
  error: true,
  userdata: { email: '' },
  errorInfo: { email: '' },
  loading: false,
  captchaToken: null,
  isCaptchaCompleted: false,
};

function ForgotPassword({ setForgotPasswordOpen }: forgotPasswordProps) {
  const [state, dispatch] = useReducer(ForgotPassReducer, initialState);
  const captchaRef = useRef<ReCAPTCHA>(null);

  const siteKey = process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY;

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    let errorMsg = '';

    errorMsg = validateEmail(value);

    dispatch({ type: 'SET_ERROR_INFO', payload: { [name]: errorMsg } });
    dispatch({ type: 'SET_USERDATA', payload: { [name]: value } });
    dispatch({ type: 'SET_ERROR', payload: !!errorMsg });
  };

  const handleSubmit = (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch({ type: 'SET_LOADING', payload: true });
    if (!state.captchaToken) {
      dispatch({ type: 'SET_LOADING', payload: false });
      MakeToast('error', 'Please complete the CAPTCHA');
      return;
    }
    if (state.userdata?.email) {
      forgotPassword({ email: state.userdata.email, token: state.captchaToken })
        .then((res) => {
          dispatch({ type: 'SET_LOADING', payload: false });
          if (res.status === 200) {
            dispatch({ type: 'RESET_FORM' });
            MakeToast('success', res?.data?.message.message);
            setTimeout(() => {
              setForgotPasswordOpen(false);
            }, 1000);
          }
        })
        .catch((res) => {
          dispatch({ type: 'SET_LOADING', payload: false });
          dispatch({ type: 'SET_CAPTCHA_TOKEN', payload: null }); // Reset CAPTCHA token
          if (captchaRef.current) captchaRef.current.reset(); // Reset CAPTCHA checkbox
          MakeToast(
            'error',
            res?.response?.data?.message?.message
              ? res?.response?.data?.message?.message
              : 'Something went wrong!',
          );
        });
    }
  };

  const onChangeCaptcha = (token: string | null) => {
    dispatch({ type: 'SET_CAPTCHA_TOKEN', payload: token });
  };

  return (
    <section className="flex flex-col">
      <form
        className="form-section bg-no-repeat bg-cover mr-0 ml-0 bg-[#f5f5f5] h-full items-end justify-center p-[52px] rounded-xl mt-4 border-[3px] border-[#A8C5DA]"
        onSubmit={handleSubmit}
      >
        <span className="font-bold text-3xl mb-[5px]">{ForgotPasswordText}</span>
        <br />
        <span className="text-[#6A6A6A] text-sm mb-3">{ForgotPasswordInfo}</span>
        <InputField
          label="Email"
          type="text"
          tooltip="Allows only valid emails"
          placeholder="Example@domain.com"
          name="email"
          handleChange={handleFieldChange}
          error={state.errorInfo?.email || ''}
        />
        <ReCAPTCHA
          ref={captchaRef}
          className="mt-2 recaptcha-container"
          sitekey={siteKey || ''}
          onChange={onChangeCaptcha}
        />
        <button
          type="submit"
          className={
            state.userdata?.email === '' ||
            state.error ||
            !state.isCaptchaCompleted ||
            !state.captchaToken
              ? 'bg-[#ccc] pointer-events-none text-[#fff] rounded-lg py-[12px] px-[60px] select-none w-full'
              : 'bg-[#0d85bd] text-[#fff] rounded-lg py-[12px] px-[60px] select-none w-full'
          }
        >
          {SubmitText}
        </button>
        <p className="text-[#6A6A6A] justify-center text-center text-sm cursor-pointer pt-2">
          {BackToLogin}&nbsp;
          <button
            type="button"
            className="text-[#F48B00] hover:underline"
            onClick={() => setForgotPasswordOpen(false)}
          >
            Login
          </button>
        </p>
      </form>
      {state.loading && createPortal(<LoaderOverlay />, document.body)}
    </section>
  );
}

export default ForgotPassword;
